import { useState } from 'react';
import { Menu, Globe, Mail, Check, Phone, MapPin, CreditCard, SquareArrowOutUpRight } from 'lucide-react';

// Language content
const currentYear = new Date().getFullYear();
const content = {
  tr: {
    nav: {
      about: 'Hakkımızda',
      product: 'Ürünümüz',
      contact: 'İletişim'
    },
    hero: {
      title: 'İnşaat Sektörü için Akıllı Optimizasyon Çözümleri',
      subtitle: 'Tebersoft, inşaat sektöründe malzeme israfını en aza indirmek için yenilikçi çözümler sunar.'
    },
    about: {
      title: 'Tebersoft Hakkında',
      description: 'Tebersoft, 2023 yılında Gaziantep\'te kurulmuş yenilikçi bir yazılım şirketidir. Ekibimiz, inşaat sektörü için akıllı, verimli ve sürdürülebilir dijital araçlar geliştirmeye odaklanmıştır. İnşaat sektöründeki verimliliği artırmak ve çevresel etkiyi azaltmak için en son teknolojileri kullanarak çözümler geliştiriyoruz.',
      mission: 'Misyonumuz',
      missionText: 'İnşaat sektöründe verimliliği artırmak ve israfı azaltmak için yenilikçi yazılım çözümleri geliştirmek.',
      values: 'Değerlerimiz',
      valuesItems: ['Yenilikçilik', 'Sürdürülebilirlik', 'Müşteri Odaklılık', 'Kalite']
    },
    product: {
      title: 'Ürünümüz: Tasnifer',
      subtitle: 'Donatı ve Çelik Profil Optimizasyonu',
      description: 'Tasnifer, inşaat sahalarında donatı ve çelik profil israfını en aza indirmek için tasarlanmış web tabanlı bir optimizasyon aracıdır. Donatı ve çelik profillerin verimli kesim planlarını oluşturarak gereksiz malzeme kullanımını azaltır ve genel inşaat verimliliğini artırır.',
      benefits: 'Faydaları',
      benefitsList: [
        {
          title: 'Malzeme Tasarrufu',
          description: 'Akıllı kesim planları ile donatı ve çelik profil israfını mümkün olan en düşük seviyeye indirir.'
        },
        {
          title: 'Zaman Tasarrufu',
          description: 'Kesim planlarının manuel hazırlanması için gereken süreyi ortadan kaldırır.'
        },
        {
          title: 'Maliyet Optimizasyonu',
          description: 'Donatı ve çelik profil israfını azaltarak proje maliyetlerini düşürür.'
        },
        {
          title: 'Sürdürülebilirlik',
          description: 'Kaynakların daha verimli kullanılmasıyla çevresel etkiyi azaltır.'
        }
      ],
      cta: 'Daha Fazla Bilgi İçin'
    },
    contact: {
      title: 'İletişim',
      address: 'Adres',
      phone: 'Telefon',
      generalManager: 'Genel Müdür',
      email: 'E-posta',
      taxId: 'Vergi Kimlik Numarası'
    },
    footer: {
      copyright: `© ${currentYear} Tebersoft. Tüm hakları saklıdır.`,
      language: 'Dil'
    }
  },
  en: {
    nav: {
      about: 'About',
      product: 'Product',
      contact: 'Contact'
    },
    hero: {
      title: 'Smart Optimization Solutions for Construction Industry',
      subtitle: 'Tebersoft offers innovative solutions to minimize material waste in the construction sector.'
    },
    about: {
      title: 'About Tebersoft',
      description: 'Tebersoft is an innovative software company founded in 2023 in Gaziantep, Türkiye. Our team focuses on developing smart, efficient, and sustainable digital tools for the construction industry. We create solutions using the latest technologies to improve efficiency in the construction sector and reduce environmental impact.',
      mission: 'Our Mission',
      missionText: 'To develop innovative software solutions that improve efficiency and reduce waste in the construction industry.',
      values: 'Our Values',
      valuesItems: ['Innovation', 'Sustainability', 'Customer Focus', 'Quality']
    },
    product: {
      title: 'Our Product: Tasnifer',
      subtitle: 'Construction Material Optimization Solution',
      description: 'Tasnifer is a web-based optimization tool designed to minimize rebar and steel profile waste on construction sites. It creates efficient cutting plans for rebars and steel profiles, reducing unnecessary rebar and steel profile usage and improving overall construction efficiency.',
      benefits: 'Benefits',
      benefitsList: [
        {
          title: 'Material Savings',
          description: 'Reduces rebar and steel profile waste to the lowest possible level with smart cutting plans.'
        },
        {
          title: 'Time Savings',
          description: 'Eliminates time required for manual preparation of cutting plans.'
        },
        {
          title: 'Cost Optimization',
          description: 'Reduces project costs by minimizing rebar and steel profile waste.'
        },
        {
          title: 'Sustainability',
          description: 'Decreases environmental impact through more efficient resource utilization.'
        }
      ],
      cta: 'Learn More'
    },
    contact: {
      title: 'Contact Us',
      address: 'Address',
      phone: 'Phone',
      generalManager: 'General Manager',
      email: 'Email',
      taxId: 'Tax ID'
    },
    footer: {
      copyright: `© ${currentYear} Tebersoft. All rights reserved.`,
      language: 'Language'
    }
  }
};

export default function App() {
  const [lang, setLang] = useState('tr');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = content[lang];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen font-sans text-gray-800">
      {/* Navigation */}
      <nav className="bg-white shadow-md fixed w-full z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              {/* Logo */}
              <img
                src="/logo512.png"
                alt="Tebersoft Logo"
                className="h-8 w-auto mr-2"
              />
              <div className="text-2xl font-bold text-primary-600">Tebersoft</div>
            </div>

            {/* Desktop menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-600 hover:text-primary-600 transition-colors">
                {t.nav.about}
              </button>
              <button onClick={() => scrollToSection('product')} className="text-gray-600 hover:text-primary-600 transition-colors">
                {t.nav.product}
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-primary-600 transition-colors">
                {t.nav.contact}
              </button>

              {/* Language switcher */}
              <div className="flex items-center space-x-2">
                <Globe size={16} className="text-gray-500" />
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="bg-transparent border-none text-gray-600 focus:outline-none cursor-pointer"
                >
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-500 hover:text-primary-600 focus:outline-none"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button
                onClick={() => scrollToSection('about')}
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 w-full text-left"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('product')}
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 w-full text-left"
              >
                {t.nav.product}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 w-full text-left"
              >
                {t.nav.contact}
              </button>

              {/* Mobile language switcher */}
              <div className="flex items-center px-3 py-2">
                <Globe size={16} className="text-gray-500 mr-2" />
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="bg-transparent border-none text-gray-600 focus:outline-none cursor-pointer"
                >
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4 pt-8 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-700 mb-4">
              {t.hero.title}
            </h1>
            <p className="text-xl text-gray-600 pt-4 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
          </div>

          <div className="mt-12 flex justify-center">
            {/* Youtube video */}
            <iframe
              width="700"
              height="394"
              src="https://www.youtube.com/embed/1qex_LTYak8?si=j7T2TKBKl7BPUdJt"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg shadow-lg"
            ></iframe>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-primary-500 mb-8">
            {t.about.title}
          </h2>

          <div className="grid md:grid-cols-2 gap-12 justify-center items-center">
            <div className="flex justify-center">
              <p className="text-gray-600 mb-6 leading-relaxed text-center md:text-left">
                {t.about.description}
              </p>
            </div>

            <div className="flex justify-center">
              <div className="mb-6 text-center md:text-left">
                <h3 className="text-xl font-semibold text-primary-600 mb-2">
                  {t.about.mission}
                </h3>
                <p className="text-gray-600">
                  {t.about.missionText}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center mt-8">
            <h3 className="text-xl font-semibold text-primary-600 mb-4 text-center">
              {t.about.values}
            </h3>
            <ul className="flex flex-wrap gap-4 justify-center">
              {t.about.valuesItems.map((value, index) => (
                <li key={index} className="flex items-center bg-primary-50 rounded-full px-4 py-2">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center mr-2">
                      <span className="text-primary-600 text-sm font-medium">{index + 1}</span>
                    </div>
                    <span className="text-gray-700">{value}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section id="product" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {t.product.title}
            </h2>
            <p className="text-xl text-primary-600">
              {t.product.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-2">
              <img
                src="tasnifer_sample_ss.png"
                alt="Tasnifer software interface"
                className="rounded-lg max-w-full h-auto"
              />
            </div>

            <div className="md:col-span-3">
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t.product.description}
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-4">
                {t.product.benefits}
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                {t.product.benefitsList.map((benefit, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm flex items-center">
                    {/* Example icon placeholder, replace with actual icon if needed */}
                    <div className="flex-shrink-0 mr-3">
                      <Check className="text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-1">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-center md:justify-start">
                <button
                  className="bg-primary-600 text-white py-2 px-6 rounded-md hover:bg-primary-700 transition-colors shadow-sm flex items-center"
                  onClick={() => window.open('https://tasnifer.com', '_blank')}
                >
                  {t.product.cta}
                  <SquareArrowOutUpRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            {t.contact.title}
          </h2>

          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="flex-shrink-0 h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-800">
                      {t.contact.address}
                    </p>
                    <p className="mt-1 text-gray-600">
                      Tebersoft Yazılım İnşaat Sanayi ve Ticaret Ltd. Şti.
                      <br />
                      Çamtepe Mh. Mahmut Tevfik Atay Bulvarı Gaziantep Teknopark No: 4/A İç Kapı No: 3 Şahinbey / Gaziantep / Türkiye
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="flex-shrink-0 h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-800">
                      {t.contact.phone}
                    </p>
                    <p className="mt-1 text-gray-600">

                      {t.contact.generalManager}: Prof. Dr. Murat Pala <br />
                      +90 505 434 66 25
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="flex-shrink-0 h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-800">
                      {t.contact.email}
                    </p>
                    <p className="mt-1 text-gray-600">
                      info@tebersoft.net
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CreditCard className="flex-shrink-0 h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-800">
                      {t.contact.taxId}
                    </p>
                    <p className="mt-1 text-gray-600">
                      8331165876
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <div className="text-2xl font-bold text-white">Tebersoft</div>
              <p className="text-gray-300 mt-1">
                {t.footer.copyright}
              </p>
            </div>

            <div className="flex items-center space-x-4 justify-center md:justify-end w-full md:w-auto">
              {/* LinkedIn link */}
              <a
                href="https://www.linkedin.com/company/tebersoft"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-primary-400 transition-colors"
                aria-label="Tebersoft LinkedIn"
              >
                <img
                  width={30}
                  height={30}
                  alt='Tebersoft LinkedIn'
                  src="linkedin.svg"
                >
                </img>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}